import { Typography } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import { useEffect, useState } from 'react'
import { useGetOne, useNotify, useRefresh } from 'react-admin'
import { Divider, FlexBox } from 'shared/components/Base/Base'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { Text } from 'shared/components/Text/Text'

import { CloudTrucksLLCMCNumber, CTINLLCMCNumber, RoleNames } from 'helpers/constants'
import { captureExceptionIfProduction, getFormattedDate } from 'helpers/utils'
import { useHasCarrierSelectionOnboarding } from 'hooks/useHasFeatureFlag'
import { changeDriverStatus, updateTeamUserGroups } from '../../../api/core'
import {
  DriverDeactivateReasons,
  DriverTransitions,
  SuspendedDriverReasons,
} from '../Transitions/DriverFSMTransitions'
import { FSMTransitionButton, XTransitionButtonProps } from './FSMTransitionButton'

const driverRevokingAccessTransitions = [
  DriverTransitions.abortDriver.transitionAction,
  DriverTransitions.deactivateDriver.transitionAction,
  DriverTransitions.rejectDriver.transitionAction,
  DriverTransitions.suspendDriver.transitionAction,
]

const isOnboardingDriver = (action: any) => action === DriverTransitions.onboardDriver.transitionAction

const isRevokeDriverAccess = (action: any) => driverRevokingAccessTransitions.includes(action)

const isDeactivateDriver = (action: any) => action === DriverTransitions.deactivateDriver.transitionAction

const isSuspendDriver = (action: any) => action === DriverTransitions.suspendDriver.transitionAction

function getCarrierLabel(mcNumber: string) {
  switch (mcNumber) {
    case CloudTrucksLLCMCNumber:
      return `CloudTrucks LLC (MC ${CloudTrucksLLCMCNumber})`
    case CTINLLCMCNumber:
      return `CT IN LLC (MC ${CTINLLCMCNumber})`
    default:
      return 'Unknown carrier'
  }
}

export const DriverTransitionButton = ({
  record,
  transitionProps,
  classes,
  loading,
  setLoading,
  children,
  onSuccess,
}: XTransitionButtonProps) => {
  const notify = useNotify()
  const refresh = useRefresh()

  /* State  */
  const [reason, setReason] = useState('')
  const [approveFleetCheck, setApproveFleetCheck] = useState(true)
  const [statusDetail, setStatusDetail] = useState('')
  const [isFormInvalid, setIsFormInvalid] = useState(false)
  const { data: team } = useGetOne('teams', record.team.id)
  const [businessOwner, setBusinessOwner] = useState('')
  const [carrierMCNumber, setCarrierMCNumber] = useState(
    record.team.carrier?.mc_number || CloudTrucksLLCMCNumber
  )
  const hasCarrierSelectionOnboarding = useHasCarrierSelectionOnboarding()

  useEffect(() => {
    if (team) {
      setBusinessOwner(getBusinessOwner(team.members))
    }
  }, [team])

  const getBusinessOwner = (members: any) => {
    if (members) {
      const membersSorted = [...members].sort(
        (a, b) => getFormattedDate(a.date_updated) - getFormattedDate(b.date_updated)
      )
      for (let member of membersSorted) {
        if (member.roles && member.roles.includes(RoleNames.owner)) {
          return member.id
        }
      }
    }
  }

  const finalConfirmationDialogContent = (
    <FlexBox flexDirection="column" alignItems="flex-start">
      <Text variant="header2" mb={1}>
        Confirm authority
      </Text>
      <Text variant="paragraph1" align="left">
        Make sure the driver is assigned to the correct authority. This can't be edited later.
      </Text>
      <br />
      <Text variant="header3">
        Driver: {record.first_name} {record.last_name}
      </Text>
      <Text variant="header3">Authority: {getCarrierLabel(carrierMCNumber)}</Text>
    </FlexBox>
  )

  const memberAlreadyInGroup = (member_id: any, members: any, group_name: any) => {
    return members.some((member: any) => member_id === member.id && member.roles.includes(group_name))
  }

  const { transitionString, transitionAction } = transitionProps

  /* Driver Transition Types */
  const isOnboardingTransition = isOnboardingDriver(transitionAction)
  const isRevokingAccessTransition = isRevokeDriverAccess(transitionAction)
  const isDriverDeactivatingTransition = isDeactivateDriver(transitionAction)
  const isDriverSuspendingTransition = isSuspendDriver(transitionAction)

  const revokeAccessLabel = transitionString + ' Reason'

  const handleSubmit = () => {
    if (!isDeactivateDriver(transitionAction) || !isSuspendDriver(transitionAction)) {
      // set status detail and reason to empty since this is not a param for unsuspending
      setStatusDetail('')
      setReason('')
    }

    const transitionTerminationReason = isRevokingAccessTransition ? reason.trim() : null
    const isReasonEmpty = isRevokingAccessTransition && transitionTerminationReason === ''
    const isStatusDetailEmpty = statusDetail === ''
    if ((isDriverDeactivatingTransition || isDriverSuspendingTransition) && isStatusDetailEmpty) {
      const errorMessage = 'Reason is required'
      setIsFormInvalid(true)
      notify(errorMessage, { type: 'error' })
    } else if (isRevokingAccessTransition && isReasonEmpty) {
      const errorMessage =
        isDriverDeactivatingTransition || isDriverSuspendingTransition
          ? 'Note is required'
          : revokeAccessLabel + ' is required'
      setIsFormInvalid(true)
      notify(errorMessage, { type: 'error' })
    } else {
      const approveFleet = isOnboardingTransition ? approveFleetCheck : null
      const carrierMC = isOnboardingTransition && hasCarrierSelectionOnboarding ? carrierMCNumber : null
      setLoading(true)
      changeDriverStatus(
        record.id,
        transitionAction,
        transitionTerminationReason,
        approveFleet,
        statusDetail,
        carrierMC
      )
        .then(() => {
          if (
            businessOwner &&
            memberAlreadyInGroup(businessOwner, team?.members, RoleNames.owner) === false
          ) {
            const teamUserGroups = {
              team_user_groups: {
                BUSINESS_OWNER: true,
                TEAM_ADMIN: true,
                DRIVER: memberAlreadyInGroup(businessOwner, team?.members, RoleNames.driver),
              },
            }
            updateTeamUserGroups(businessOwner, teamUserGroups).catch((error) => {
              console.error(error)
              notify(JSON.stringify(error.response.data), { type: 'error' })
            })
          }
        })
        .then(async () => {
          refresh()
          await onSuccess?.()
          setLoading(false)
          notify('Transition successful!', { type: 'success' })
        })
        .catch((error) => {
          setLoading(false)
          notify(JSON.stringify(error?.response?.data), { type: 'error' })
          if (
            error?.response?.data.length > 0 &&
            (error?.response?.data?.assigned_truck === undefined ||
              error?.response?.data?.assigned_truck[0] !== 'Driver must have an assigned truck')
          ) {
            captureExceptionIfProduction(error)
          }
        })
    }
  }

  const handleReasonEntry = (event: any) => {
    setReason(event.target.value)
  }
  const approveFleet = (event: any) => {
    setApproveFleetCheck(event.target.checked)
  }
  const handleStatusDetailReasonChange = (event: any) => {
    setStatusDetail(event.target.value)
  }

  const statusReasons = isDriverDeactivatingTransition
    ? DriverDeactivateReasons
    : isDriverSuspendingTransition
    ? SuspendedDriverReasons
    : null

  const getCarrierSelectionOptions = () => {
    if (record.team.carrier) {
      return [{ value: record.team.carrier.mc_number, label: getCarrierLabel(record.team.carrier.mc_number) }]
    }
    return [
      { value: CloudTrucksLLCMCNumber, label: getCarrierLabel(CloudTrucksLLCMCNumber) },
      { value: CTINLLCMCNumber, label: getCarrierLabel(CTINLLCMCNumber) },
    ]
  }

  return (
    <FSMTransitionButton
      handleSubmit={handleSubmit}
      record={record}
      transitionAllowed={!isRevokingAccessTransition}
      transitionProps={transitionProps}
      className={classes?.root}
      loading={loading}
      button={children}
      finalConfirmation={isOnboardingTransition && hasCarrierSelectionOnboarding && !record.team.carrier}
      finalConfirmationDialogContent={finalConfirmationDialogContent}
    >
      {isRevokingAccessTransition && (
        <div>
          {statusReasons && (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="status_detail_label">Select Reason</InputLabel>
              <Select
                label="Select Reason"
                id="status_detail"
                labelId="status_detail_label"
                onChange={handleStatusDetailReasonChange}
                value={statusDetail}
                required
                error={isFormInvalid}
                fullWidth
              >
                {Object.keys(statusReasons).map((key) => (
                  <MenuItem value={statusReasons[key].id} key={statusReasons[key].id}>
                    {statusReasons[key].name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth variant="outlined">
            <TextField
              id="status_reason"
              label="Note"
              helperText={isFormInvalid && revokeAccessLabel + ' is required'}
              multiline
              fullWidth
              value={reason}
              rows={4}
              placeholder="Note"
              variant="outlined"
              style={{ marginTop: '10px' }}
              required
              onChange={handleReasonEntry}
            />
          </FormControl>
        </div>
      )}
      {isOnboardingTransition && team && (
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={approveFleetCheck}
                onChange={approveFleet}
                name="approveFleet"
                color="primary"
              />
            }
            label="Change the fleet status to Approved"
          />
          <Divider mt={4} mb={4} />

          <Typography>
            <b>Assign the fleet's business owner</b>
          </Typography>

          <br />

          <FormControl fullWidth>
            <InputLabel>Business owner registered agent</InputLabel>
            <Select
              id="businessOwner"
              label="businessOwner"
              onChange={(e) => setBusinessOwner(e.target.value as string)}
              value={businessOwner}
            >
              {Object.keys(team.members).map((key) => (
                <MenuItem value={team.members[key].id} key={key}>
                  {team.members[key].first_name + ' ' + team.members[key].last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasCarrierSelectionOnboarding && (
            <>
              <Divider mt={4} mb={4} />
              <RadioButtonGroup
                label={
                  record.team.carrier
                    ? "This driver's fleet already has an assigned authority"
                    : 'Which authority will this driver operate under?'
                }
                options={getCarrierSelectionOptions()}
                onChange={(e) => setCarrierMCNumber(e.target.value)}
                value={carrierMCNumber}
                noMargin
                isDisabled={record.team.carrier}
              />
            </>
          )}
        </div>
      )}
    </FSMTransitionButton>
  )
}
